import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AircraftType, Airstrip, ScheduledRoute } from '../../types';
import { scheduledSeatService, SeatInventoryItem, RouteScheduleSummaryItem } from '../../services/scheduledSeatService';

type RoutesTabProps = {
  airstrips: Airstrip[];
  aircrafts: AircraftType[];
  routes: ScheduledRoute[];
  isImportingRoutes: boolean;
  routeImportSummary: {
    created: number;
    updated: number;
    skipped: number;
    processed: number;
    errors: Array<{ row: number; message: string }>;
  } | null;
  isAddingRoute: boolean;
  newRoute: Omit<ScheduledRoute, 'id'>;
  onOpenAddRoute: () => void;
  onCloseAddRoute: () => void;
  onUpdateNewRoute: (updates: Partial<Omit<ScheduledRoute, 'id'>>) => void;
  onToggleRouteDay: (day: number) => void;
  onCreateRoute: () => void;
  onDeleteRoute: (id: string) => void;
  onDeleteAllRoutes: () => void;
  onUpdateRouteRate: (id: string, rate: number) => void;
  onUpdateRouteFields: (id: string, updates: Partial<ScheduledRoute>) => void;
  onImportRoutesFile: (file: File) => void;
  onCloseRouteImportSummary: () => void;
  onUpsertRouteAircraftRate: (routeId: string, aircraftId: string, ratePerNmUSD: number, ratePerHourUSD: number) => void;
};

export const RoutesTab: React.FC<RoutesTabProps> = ({
  airstrips,
  aircrafts,
  routes,
  isImportingRoutes,
  routeImportSummary,
  isAddingRoute,
  newRoute,
  onOpenAddRoute,
  onCloseAddRoute,
  onUpdateNewRoute,
  onToggleRouteDay,
  onCreateRoute,
  onDeleteRoute,
  onDeleteAllRoutes,
  onUpdateRouteRate,
  onUpdateRouteFields,
  onImportRoutesFile,
  onCloseRouteImportSummary,
  onUpsertRouteAircraftRate,
}) => {
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [onlyWithSchedules, setOnlyWithSchedules] = useState(false);
  const [expandedOrigins, setExpandedOrigins] = useState<Record<string, boolean>>({});
  const [expandedAircraftByRoute, setExpandedAircraftByRoute] = useState<Record<string, boolean>>({});
  const [scheduleSummaryMap, setScheduleSummaryMap] = useState<Record<string, { totalSlots: number; upcomingSlots: number }>>({});
  const [scheduleRoute, setScheduleRoute] = useState<ScheduledRoute | null>(null);
  const [scheduleDate, setScheduleDate] = useState(new Date().toISOString().slice(0, 10));
  const [scheduleMode, setScheduleMode] = useState<'ONE_DAY' | 'RECURRING'>('ONE_DAY');
  const [scheduleRangeStart, setScheduleRangeStart] = useState(new Date().toISOString().slice(0, 10));
  const [scheduleRangeEnd, setScheduleRangeEnd] = useState(new Date().toISOString().slice(0, 10));
  const [scheduleWeekdays, setScheduleWeekdays] = useState<number[]>([]);
  const [scheduleDepartureTime, setScheduleDepartureTime] = useState('08:00');
  const [scheduleSeats, setScheduleSeats] = useState(4);
  const [scheduleAircraftType, setScheduleAircraftType] = useState('');
  const [isPublishingSchedule, setIsPublishingSchedule] = useState(false);
  const [routeSchedules, setRouteSchedules] = useState<SeatInventoryItem[]>([]);
  const [isLoadingRouteSchedules, setIsLoadingRouteSchedules] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState('');
  const [editingScheduleDepartureAt, setEditingScheduleDepartureAt] = useState('');
  const [editingScheduleSeats, setEditingScheduleSeats] = useState(0);
  const [editingSchedulePrice, setEditingSchedulePrice] = useState(0);

  const airstripByCode = useMemo(() => {
    const result: Record<string, Airstrip> = {};
    airstrips.forEach((airstrip) => {
      result[airstrip.code] = airstrip;
    });
    return result;
  }, [airstrips]);

  const toRadians = (value: number) => (value * Math.PI) / 180;
  const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const earthRadiusKm = 6371;
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusKm * c;
  };

  const getRouteMetrics = (fromCode: string, toCode: string) => {
    const from = airstripByCode[fromCode];
    const to = airstripByCode[toCode];
    if (!from || !to) {
      return { distanceKm: 0, distanceNm: 0, blockTimeMinutes: 0 };
    }
    const distanceKm = haversineKm(from.latitude, from.longitude, to.latitude, to.longitude);
    const distanceNm = distanceKm / 1.852;
    const blockTimeMinutes = Math.round((distanceNm / 120) * 60);
    return {
      distanceKm: Number(distanceKm.toFixed(2)),
      distanceNm: Number(distanceNm.toFixed(2)),
      blockTimeMinutes,
    };
  };

  const newRouteMetrics = getRouteMetrics(newRoute.from, newRoute.to);

  const filteredRoutes = useMemo(() => {
    const q = search.trim().toLowerCase();
    const isScheduledKeyword = ['scheduled', 'schedule', 'has schedule'].includes(q);
    const getSummary = (route: ScheduledRoute) =>
      scheduleSummaryMap[`id:${route.id}`] || scheduleSummaryMap[`ft:${route.from}->${route.to}`] || { totalSlots: 0, upcomingSlots: 0 };
    return routes
      .filter((route) => (showInactive ? true : route.isActive !== false))
      .filter((route) => {
        if (!onlyWithSchedules && !isScheduledKeyword) return true;
        return getSummary(route).totalSlots > 0;
      })
      .filter((route) => {
        if (!q) return true;
        if (isScheduledKeyword) return true;
        const fromName = airstripByCode[route.from]?.name || '';
        const toName = airstripByCode[route.to]?.name || '';
        const summary = getSummary(route);
        const scheduleText = summary.totalSlots > 0 ? `scheduled ${summary.totalSlots} ${summary.upcomingSlots}` : '';
        return [route.from, route.to, fromName, toName, scheduleText]
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to));
  }, [routes, showInactive, onlyWithSchedules, search, airstripByCode, scheduleSummaryMap]);

  const groupedRoutes = useMemo(() => {
    const groups: Record<string, ScheduledRoute[]> = {};
    filteredRoutes.forEach((route) => {
      if (!groups[route.from]) groups[route.from] = [];
      groups[route.from].push(route);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredRoutes]);

  useEffect(() => {
    setExpandedOrigins((prev) => {
      const next = { ...prev };
      for (const [origin] of groupedRoutes) {
        if (next[origin] === undefined) next[origin] = true;
      }
      return next;
    });
  }, [groupedRoutes]);

  const totalActive = routes.filter((r) => r.isActive !== false).length;
  const totalInactive = routes.length - totalActive;
  const totalWithSchedules = useMemo(() => {
    return routes.filter((route) => {
      const summary = scheduleSummaryMap[`id:${route.id}`] || scheduleSummaryMap[`ft:${route.from}->${route.to}`];
      return Number(summary?.totalSlots || 0) > 0;
    }).length;
  }, [routes, scheduleSummaryMap]);

  const getRouteAircraftTypes = (route?: ScheduledRoute | null) => {
    if (!route) return [] as Array<{ name: string; maxPax: number }>;
    const ids = (route.aircraftRates || []).map((r) => r.aircraftId);
    const source = ids.length ? aircrafts.filter((a) => ids.includes(a.id)) : aircrafts;
    const map = new Map<string, number>();
    source.forEach((a) => {
      const key = String(a.name || '').trim();
      if (!key) return;
      map.set(key, Math.max(map.get(key) || 0, Number(a.maxPax || 0)));
    });
    return Array.from(map.entries())
      .map(([name, maxPax]) => ({ name, maxPax }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const routeAircraftTypes = useMemo(() => getRouteAircraftTypes(scheduleRoute), [scheduleRoute, aircrafts]);

  const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const normalizeTimeValue = (value?: string | null) => {
    const raw = String(value || '').trim();
    const matched = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (!matched) return '';
    const hh = Number(matched[1]);
    const mm = Number(matched[2]);
    if (!Number.isInteger(hh) || !Number.isInteger(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return '';
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };

  const collectScheduleDates = (): string[] => {
    if (scheduleMode === 'ONE_DAY') {
      return scheduleDate ? [scheduleDate] : [];
    }
    const start = new Date(`${scheduleRangeStart}T00:00:00.000Z`);
    const end = new Date(`${scheduleRangeEnd}T00:00:00.000Z`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
    const weekdays = scheduleWeekdays.length ? scheduleWeekdays : [1, 2, 3, 4, 5, 6, 0];
    const out: string[] = [];
    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
      if (weekdays.includes(d.getUTCDay())) out.push(d.toISOString().slice(0, 10));
    }
    return out;
  };

  const loadRouteSchedules = async (route: ScheduledRoute) => {
    try {
      setIsLoadingRouteSchedules(true);
      const rows = await scheduledSeatService.listInventory({
        limit: 200,
        source: 'FIXED_ROUTE',
        fromCode: route.from,
        toCode: route.to,
      });
      const filtered = rows
        .filter((r) => {
          const metadataRouteId = String((r.metadata as any)?.routeId || '');
          if (metadataRouteId) return metadataRouteId === route.id;
          return r.fromCode === route.from && r.toCode === route.to;
        })
        .sort((a, b) => new Date(a.departureAt).getTime() - new Date(b.departureAt).getTime());
      setRouteSchedules(filtered);
    } catch {
      setRouteSchedules([]);
    } finally {
      setIsLoadingRouteSchedules(false);
    }
  };

  const loadScheduleSummary = async () => {
    try {
      const rows = await scheduledSeatService.listRouteSummary({ source: 'FIXED_ROUTE' });
      const next: Record<string, { totalSlots: number; upcomingSlots: number }> = {};
      rows.forEach((row: RouteScheduleSummaryItem) => {
        const routeId = String(row.routeId || '').trim();
        const fromCode = String(row.fromCode || '').toUpperCase();
        const toCode = String(row.toCode || '').toUpperCase();
        if (routeId) next[`id:${routeId}`] = { totalSlots: Number(row.totalSlots || 0), upcomingSlots: Number(row.upcomingSlots || 0) };
        next[`ft:${fromCode}->${toCode}`] = { totalSlots: Number(row.totalSlots || 0), upcomingSlots: Number(row.upcomingSlots || 0) };
      });
      setScheduleSummaryMap(next);
    } catch {
      setScheduleSummaryMap({});
    }
  };

  useEffect(() => {
    loadScheduleSummary();
  }, [routes.length]);

  const toDateTimeLocalValue = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openScheduleModal = (route: ScheduledRoute) => {
    setScheduleRoute(route);
    setScheduleDate(new Date().toISOString().slice(0, 10));
    setScheduleMode('ONE_DAY');
    setScheduleRangeStart(new Date().toISOString().slice(0, 10));
    setScheduleRangeEnd(new Date().toISOString().slice(0, 10));
    setScheduleDepartureTime(normalizeTimeValue(route.departureTime) || '08:00');
    setScheduleWeekdays(Array.isArray(route.daysOfWeek) && route.daysOfWeek.length ? [...route.daysOfWeek].sort((a, b) => a - b) : [1, 2, 3, 4, 5, 6, 0]);
    const types = getRouteAircraftTypes(route);
    setScheduleAircraftType(types[0]?.name || '');
    setScheduleSeats(Math.max(1, Number(types[0]?.maxPax || 4)));
    setEditingScheduleId('');
    loadRouteSchedules(route);
  };

  const publishScheduleFromRoute = async () => {
    if (!scheduleRoute) return;
    try {
      setIsPublishingSchedule(true);
      const depTime = normalizeTimeValue(scheduleDepartureTime) || normalizeTimeValue(scheduleRoute.departureTime) || '08:00';
      const dates = collectScheduleDates();
      if (!dates.length) {
        alert('No valid schedule dates selected.');
        return;
      }
      for (const d of dates) {
        const departureAt = new Date(`${d}T${depTime}:00.000Z`).toISOString();
        await scheduledSeatService.createInventory({
          source: 'FIXED_ROUTE',
          status: 'PUBLISHED',
          fromCode: scheduleRoute.from,
          toCode: scheduleRoute.to,
          departureAt,
          totalSeats: Math.max(1, Number(scheduleSeats || 1)),
          availableSeats: Math.max(1, Number(scheduleSeats || 1)),
          metadata: {
            routeId: scheduleRoute.id,
            routeSource: 'SYSTEM_REGISTRY',
            aircraftType: scheduleAircraftType || null,
          },
        });
      }
      alert(`${dates.length} scheduled flight(s) published for ${scheduleRoute.from} -> ${scheduleRoute.to}.`);
      await loadRouteSchedules(scheduleRoute);
      await loadScheduleSummary();
    } catch (error: any) {
      alert(error?.response?.data?.error || error?.message || 'Failed to publish schedule from route');
    } finally {
      setIsPublishingSchedule(false);
    }
  };

  const cancelRouteSchedule = async (slotId: string) => {
    if (!scheduleRoute) return;
    if (!confirm('Cancel this scheduled flight slot?')) return;
    await scheduledSeatService.setInventoryStatus(slotId, 'CANCELLED');
    await loadRouteSchedules(scheduleRoute);
    await loadScheduleSummary();
  };

  const saveRouteScheduleEdits = async () => {
    if (!scheduleRoute || !editingScheduleId) return;
    await scheduledSeatService.updateInventory(editingScheduleId, {
      departureAt: editingScheduleDepartureAt ? new Date(editingScheduleDepartureAt).toISOString() : undefined,
      totalSeats: Math.max(1, Number(editingScheduleSeats || 1)),
      availableSeats: Math.max(0, Number(editingScheduleSeats || 1)),
      seatPriceUSD: Math.max(0, Number(editingSchedulePrice || 0)),
    });
    setEditingScheduleId('');
    await loadRouteSchedules(scheduleRoute);
    await loadScheduleSummary();
  };

  return (
    <div className="space-y-8">
      {scheduleRoute && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center bg-slate-900/80 backdrop-blur-xl p-6">
          <div className="bg-white rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl animate-in zoom-in-95">
            <div className="p-6 bg-indigo-600 text-white flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-black tracking-tight">Create Scheduled Flight</h3>
                <p className="text-[10px] font-bold text-indigo-100 uppercase tracking-[0.2em] mt-1">
                  {scheduleRoute.from} → {scheduleRoute.to}
                </p>
              </div>
              <button onClick={() => setScheduleRoute(null)} className="text-white/70 hover:text-white">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">From</label>
                  <input className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm uppercase" value={scheduleRoute.from} readOnly />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">To</label>
                  <input className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm uppercase" value={scheduleRoute.to} readOnly />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Schedule Mode</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setScheduleMode('ONE_DAY')} className={`rounded-lg border px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] ${scheduleMode === 'ONE_DAY' ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600'}`}>One Day</button>
                    <button type="button" onClick={() => setScheduleMode('RECURRING')} className={`rounded-lg border px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] ${scheduleMode === 'RECURRING' ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600'}`}>Recurring Days</button>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Departure Time</label>
                  <input type="time" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={scheduleDepartureTime} onChange={(e) => setScheduleDepartureTime(e.target.value)} />
                </div>

                {scheduleMode === 'ONE_DAY' ? (
                  <div className="col-span-2 space-y-1">
                    <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Flight Date (Single Day)</label>
                    <input type="date" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} />
                  </div>
                ) : (
                  <>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Start Date</label>
                      <input type="date" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={scheduleRangeStart} onChange={(e) => setScheduleRangeStart(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">End Date</label>
                      <input type="date" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={scheduleRangeEnd} onChange={(e) => setScheduleRangeEnd(e.target.value)} />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Days Of Week</label>
                      <div className="flex gap-2 flex-wrap">
                        {weekdayLabels.map((d, i) => (
                          <button
                            key={`${d}-${i}`}
                            type="button"
                            onClick={() => setScheduleWeekdays((prev) => prev.includes(i) ? prev.filter(v => v !== i) : [...prev, i].sort((a, b) => a - b))}
                            className={`w-10 h-10 rounded-lg border text-[10px] font-black uppercase ${scheduleWeekdays.includes(i) ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-500'}`}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Aircraft Type</label>
                  <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={scheduleAircraftType} onChange={(e) => {
                    const type = e.target.value;
                    setScheduleAircraftType(type);
                    const found = routeAircraftTypes.find((x) => x.name === type);
                    if (found?.maxPax) setScheduleSeats(Math.max(1, found.maxPax));
                  }}>
                    <option value="">Select type (optional)</option>
                    {routeAircraftTypes.map((t) => (
                      <option key={t.name} value={t.name}>{t.name} · {t.maxPax} seats</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Available Seats</label>
                  <input type="number" min={1} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={scheduleSeats} onChange={(e) => setScheduleSeats(Math.max(1, Number(e.target.value) || 1))} />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={publishScheduleFromRoute}
                  disabled={isPublishingSchedule}
                  className="rounded-xl bg-indigo-600 px-4 py-2 text-[10px] font-black uppercase tracking-[0.14em] text-white hover:bg-indigo-500 disabled:opacity-60"
                >
                  {isPublishingSchedule ? 'Publishing...' : 'Publish Scheduled Flight'}
                </button>
                <button
                  onClick={() => setScheduleRoute(null)}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-700"
                >
                  Close
                </button>
              </div>

              <div className="pt-2 border-t border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Published Schedules For This Route</p>
                  <span className="text-xs font-bold text-slate-500">{routeSchedules.length}</span>
                </div>
                {isLoadingRouteSchedules ? (
                  <div className="text-xs text-slate-500">Loading schedules...</div>
                ) : routeSchedules.length === 0 ? (
                  <div className="text-xs text-slate-500">No schedules published yet for this route.</div>
                ) : (
                  <div className="max-h-56 overflow-auto space-y-2">
                    {routeSchedules.map((slot) => (
                      <div key={slot.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-black text-slate-700">{new Date(slot.departureAt).toLocaleString()}</span>
                          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase ${slot.status === 'PUBLISHED' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}>{slot.status}</span>
                          <span className="text-slate-500">Seats {slot.availableSeats}/{slot.totalSeats}</span>
                          <span className="text-slate-500">USD {slot.seatPriceUSD}</span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingScheduleId(slot.id);
                              setEditingScheduleDepartureAt(toDateTimeLocalValue(slot.departureAt));
                              setEditingScheduleSeats(Number(slot.totalSeats || 1));
                              setEditingSchedulePrice(Number(slot.seatPriceUSD || 0));
                            }}
                            className="rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] font-black uppercase text-sky-700"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => cancelRouteSchedule(slot.id)}
                            className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[10px] font-black uppercase text-rose-700"
                          >
                            Cancel Slot
                          </button>
                        </div>
                        {editingScheduleId === slot.id && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <input type="datetime-local" className="col-span-2 rounded-md border border-slate-300 px-2 py-1 text-xs" value={editingScheduleDepartureAt} onChange={(e) => setEditingScheduleDepartureAt(e.target.value)} />
                            <input type="number" min={1} className="rounded-md border border-slate-300 px-2 py-1 text-xs" value={editingScheduleSeats} onChange={(e) => setEditingScheduleSeats(Math.max(1, Number(e.target.value) || 1))} />
                            <input type="number" min={0} className="rounded-md border border-slate-300 px-2 py-1 text-xs" value={editingSchedulePrice} onChange={(e) => setEditingSchedulePrice(Math.max(0, Number(e.target.value) || 0))} />
                            <button type="button" onClick={saveRouteScheduleEdits} className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase text-emerald-700">Save</button>
                            <button type="button" onClick={() => { setEditingScheduleId(''); setEditingScheduleDepartureAt(''); }} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[10px] font-black uppercase text-slate-600">Close</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isAddingRoute && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-900/80 backdrop-blur-xl p-6">
          <div className="bg-white rounded-[4rem] w-full max-w-2xl overflow-hidden shadow-2xl animate-in zoom-in-95">
            <div className="p-10 bg-indigo-600 text-white flex justify-between items-center">
              <div>
                <h3 className="text-3xl font-black uppercase tracking-tight">Route Synthesis</h3>
                <p className="text-[10px] font-bold text-indigo-200 uppercase tracking-[0.3em] mt-2">Scheduled Network Architecture</p>
              </div>
              <button onClick={onCloseAddRoute} className="text-white/40 hover:text-white transition-all">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="p-10 space-y-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Origin</label>
                  <select className="w-full bg-slate-50 rounded-2xl p-5 font-bold outline-none appearance-none" value={newRoute.from} onChange={e => onUpdateNewRoute({ from: e.target.value })}>
                    {airstrips.map(as => <option key={as.code} value={as.code}>{as.code} - {as.name}</option>)}
                  </select>
                </div>
                <div className="space-y-4">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Destination</label>
                  <select className="w-full bg-slate-50 rounded-2xl p-5 font-bold outline-none appearance-none" value={newRoute.to} onChange={e => onUpdateNewRoute({ to: e.target.value })}>
                    {airstrips.map(as => <option key={as.code} value={as.code}>{as.code} - {as.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Operating Days</label>
                <div className="flex gap-3 flex-wrap">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, i) => (
                    <button
                      key={`${label}-${i}`}
                      type="button"
                      onClick={() => onToggleRouteDay(i)}
                      className={`w-12 h-12 rounded-xl text-[10px] font-black uppercase transition-all border ${newRoute.daysOfWeek.includes(i) ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg' : 'bg-slate-50 text-slate-400 border-slate-100'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Rack Rate (USD)</label>
                <input type="number" className="w-full bg-slate-50 rounded-2xl p-5 font-black font-mono outline-none" value={newRoute.rackRateUSD} onChange={e => onUpdateNewRoute({ rackRateUSD: Number(e.target.value) || 0 })} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Rack Child (USD)</label>
                  <input type="number" className="w-full bg-slate-50 rounded-xl p-3 font-bold font-mono outline-none" value={Number(newRoute.rackChildUSD || 0)} onChange={e => onUpdateNewRoute({ rackChildUSD: Number(e.target.value) || 0 })} />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Taxes (USD)</label>
                  <input type="number" className="w-full bg-slate-50 rounded-xl p-3 font-bold font-mono outline-none" value={Number(newRoute.taxesUSD || 0)} onChange={e => onUpdateNewRoute({ taxesUSD: Number(e.target.value) || 0 })} />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Basic Adult (USD)</label>
                  <input type="number" className="w-full bg-slate-50 rounded-xl p-3 font-bold font-mono outline-none" value={Number(newRoute.basicAdultUSD || 0)} onChange={e => onUpdateNewRoute({ basicAdultUSD: Number(e.target.value) || 0 })} />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Basic Child (USD)</label>
                  <input type="number" className="w-full bg-slate-50 rounded-xl p-3 font-bold font-mono outline-none" value={Number(newRoute.basicChildUSD || 0)} onChange={e => onUpdateNewRoute({ basicChildUSD: Number(e.target.value) || 0 })} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-2xl p-4"><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Distance (NM)</p><p className="text-lg font-black text-slate-800 font-mono">{newRouteMetrics.distanceNm}</p></div>
                <div className="bg-slate-50 rounded-2xl p-4"><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Distance (KM)</p><p className="text-lg font-black text-slate-800 font-mono">{newRouteMetrics.distanceKm}</p></div>
                <div className="bg-slate-50 rounded-2xl p-4"><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Est. Block (MIN)</p><p className="text-lg font-black text-slate-800 font-mono">{newRouteMetrics.blockTimeMinutes}</p></div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Departure Time</label>
                  <input type="time" className="w-full bg-slate-50 rounded-2xl p-5 font-bold outline-none" value={newRoute.departureTime} onChange={e => onUpdateNewRoute({ departureTime: e.target.value })} />
                </div>
                <div className="space-y-4">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Arrival Time</label>
                  <input type="time" className="w-full bg-slate-50 rounded-2xl p-5 font-bold outline-none" value={newRoute.arrivalTime} onChange={e => onUpdateNewRoute({ arrivalTime: e.target.value })} />
                </div>
              </div>

              <button onClick={onCreateRoute} className="w-full py-8 bg-indigo-600 text-white rounded-[2.5rem] font-black text-[10px] uppercase tracking-[0.4em] hover:bg-indigo-500 transition-all shadow-xl">
                Commit Route to Ledger
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-indigo-50/30">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-3xl font-black tracking-tight text-slate-900">Scheduled Route Registry</h3>
              <p className="text-sm font-medium text-slate-600">Commercial Network Matrix</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => importInputRef.current?.click()}
                disabled={isImportingRoutes}
                className="px-4 py-2 bg-white text-slate-700 rounded-xl text-sm font-bold border border-slate-300 hover:border-sky-400 hover:text-sky-700 transition-all flex items-center gap-2 shadow-sm disabled:opacity-60"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3 3m0 0l-3-3m3 3V9" /></svg>
                {isImportingRoutes ? 'Importing...' : 'Import Excel'}
              </button>
              <button
                onClick={onDeleteAllRoutes}
                className="px-4 py-2 bg-rose-50 text-rose-700 rounded-xl text-sm font-bold border border-rose-200 hover:bg-rose-100 transition-all flex items-center gap-2 shadow-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                Delete All
              </button>
              <button
                onClick={onOpenAddRoute}
                className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl text-sm font-bold hover:from-indigo-700 hover:to-blue-700 transition-all flex items-center gap-2 shadow-lg shadow-indigo-500/40"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                New Route
              </button>
              <input
                ref={importInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) onImportRoutesFile(file);
                  event.currentTarget.value = '';
                }}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_auto_auto] gap-3 items-center">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code or airstrip name..."
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-indigo-500"
            />
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <input type="checkbox" checked={onlyWithSchedules} onChange={(e) => setOnlyWithSchedules(e.target.checked)} />
              Only scheduled
            </label>
            <div className="flex items-center gap-2">
              <button onClick={() => setExpandedOrigins(Object.fromEntries(groupedRoutes.map(([k]) => [k, true])))} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900">Expand all</button>
              <button onClick={() => setExpandedOrigins(Object.fromEntries(groupedRoutes.map(([k]) => [k, false])))} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900">Collapse all</button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs font-bold text-slate-600">
            <span className="rounded-full border border-slate-300 bg-white px-3 py-1">Total {routes.length}</span>
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-700">Active {totalActive}</span>
            <span className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1">Inactive {totalInactive}</span>
            <span className="rounded-full border border-indigo-300 bg-indigo-50 px-3 py-1 text-indigo-700">Scheduled {totalWithSchedules}</span>
            <span className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-indigo-700">Shown {filteredRoutes.length}</span>
          </div>
        </div>

        {routeImportSummary && (
          <div className="mx-6 my-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-xs text-slate-700">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-bold text-sky-700">Import summary: processed {routeImportSummary.processed}, created {routeImportSummary.created}, updated {routeImportSummary.updated}, skipped {routeImportSummary.skipped}</p>
                {!!routeImportSummary.errors.length && <p className="mt-1 text-rose-600">{routeImportSummary.errors.length} row(s) had issues. First: row {routeImportSummary.errors[0].row} - {routeImportSummary.errors[0].message}</p>}
              </div>
              <button onClick={onCloseRouteImportSummary} className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900">Close</button>
            </div>
          </div>
        )}

        {!filteredRoutes.length ? (
          <div className="p-14 text-center text-slate-500">
            <p className="text-base font-semibold">No routes match current filters.</p>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {groupedRoutes.map(([origin, originRoutes]) => {
              const isExpanded = expandedOrigins[origin] !== false;
              const originName = airstripByCode[origin]?.name || '';
              return (
                <div key={origin} className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm">
                  <button
                    onClick={() => setExpandedOrigins((prev) => ({ ...prev, [origin]: !isExpanded }))}
                    className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-slate-50 to-indigo-50/20 hover:from-slate-100 hover:to-indigo-50/30"
                  >
                    <div className="flex items-center gap-3 text-left">
                      <span className="inline-flex items-center justify-center h-8 min-w-8 rounded-lg bg-indigo-600 px-2 text-xs font-black text-white">{origin}</span>
                      <div>
                        <p className="text-sm font-black text-slate-900">{originName || origin}</p>
                        <p className="text-xs font-semibold text-slate-500">{originRoutes.length} route(s)</p>
                      </div>
                    </div>
                    <svg className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
                  </button>

                  {isExpanded && (
                    <div className="p-4 grid grid-cols-1 xl:grid-cols-2 gap-4 bg-slate-50/40">
                      {originRoutes.map((route) => {
                        const metrics = {
                          distanceNm: route.distanceNm ?? getRouteMetrics(route.from, route.to).distanceNm,
                          distanceKm: route.distanceKm ?? getRouteMetrics(route.from, route.to).distanceKm,
                          blockTimeMinutes: route.blockTimeMinutes ?? getRouteMetrics(route.from, route.to).blockTimeMinutes,
                        };
                        const routeKey = route.id;
                        const aircraftExpanded = expandedAircraftByRoute[routeKey] === true;
                        const toName = airstripByCode[route.to]?.name || route.to;
                        const scheduleInfo = scheduleSummaryMap[`id:${route.id}`] || scheduleSummaryMap[`ft:${route.from}->${route.to}`] || { totalSlots: 0, upcomingSlots: 0 };
                        const hasSchedule = scheduleInfo.totalSlots > 0;

                        return (
                          <div key={route.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-lg font-black text-slate-900">{route.from}{' -> '}{route.to}</p>
                                <p className="text-xs font-medium text-slate-500">{toName}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${route.isActive !== false ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-slate-100 text-slate-500'}`}>
                                  {route.isActive !== false ? 'Active' : 'Inactive'}
                                </span>
                                {hasSchedule && (
                                  <span className="rounded-full border border-sky-300 bg-sky-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-sky-700">
                                    Scheduled {scheduleInfo.upcomingSlots}/{scheduleInfo.totalSlots}
                                  </span>
                                )}
                                <button
                                  onClick={() => openScheduleModal(route)}
                                  className="rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-black text-indigo-700 hover:bg-indigo-100"
                                >
                                  Create / Manage
                                </button>
                                <button
                                  onClick={() => {
                                    if (confirm(`Delete route ${route.from} -> ${route.to}? This action cannot be undone.`)) onDeleteRoute(route.id);
                                  }}
                                  className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-black text-rose-700 hover:bg-rose-100"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>

                            <div className="mt-3 grid grid-cols-3 gap-2">
                              <div className="rounded-lg border border-sky-200 bg-sky-50 p-2"><p className="text-[10px] font-black text-slate-500">NM</p><p className="text-base font-black text-slate-900">{metrics.distanceNm}</p></div>
                              <div className="rounded-lg border border-sky-200 bg-sky-50 p-2"><p className="text-[10px] font-black text-slate-500">KM</p><p className="text-base font-black text-slate-900">{metrics.distanceKm}</p></div>
                              <div className="rounded-lg border border-sky-200 bg-sky-50 p-2"><p className="text-[10px] font-black text-slate-500">BLOCK</p><p className="text-base font-black text-slate-900">{metrics.blockTimeMinutes}m</p></div>
                            </div>

                            <div className="mt-3">
                              <label className="text-xs font-bold text-slate-600">Rack Rate (USD)</label>
                              <div className="mt-1 flex items-center gap-2">
                                <span className="text-slate-500 font-black">$</span>
                                <input
                                  type="number"
                                  value={route.rackRateUSD}
                                  onChange={(e) => onUpdateRouteRate(route.id, Number(e.target.value))}
                                  className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono font-black text-slate-900 outline-none focus:border-indigo-500"
                                />
                              </div>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[10px] font-bold text-slate-500">Rack Child</label>
                                <input
                                  type="number"
                                  defaultValue={Number(route.rackChildUSD || 0)}
                                  onBlur={(e) => onUpdateRouteFields(route.id, { rackChildUSD: Number(e.target.value) || 0 })}
                                  className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-slate-500">Taxes</label>
                                <input
                                  type="number"
                                  defaultValue={Number(route.taxesUSD || 0)}
                                  onBlur={(e) => onUpdateRouteFields(route.id, { taxesUSD: Number(e.target.value) || 0 })}
                                  className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-slate-500">Basic Adult</label>
                                <input
                                  type="number"
                                  defaultValue={Number(route.basicAdultUSD || 0)}
                                  onBlur={(e) => onUpdateRouteFields(route.id, { basicAdultUSD: Number(e.target.value) || 0 })}
                                  className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-slate-500">Basic Child</label>
                                <input
                                  type="number"
                                  defaultValue={Number(route.basicChildUSD || 0)}
                                  onBlur={(e) => onUpdateRouteFields(route.id, { basicChildUSD: Number(e.target.value) || 0 })}
                                  className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                                />
                              </div>
                            </div>

                            <div className="mt-2">
                              <label className="text-[10px] font-bold text-slate-500">Notes</label>
                              <input
                                type="text"
                                defaultValue={route.notes || ''}
                                onBlur={(e) => onUpdateRouteFields(route.id, { notes: e.target.value })}
                                placeholder="Via Arusha, baggage limit, etc."
                                className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 outline-none focus:border-indigo-500"
                              />
                            </div>

                            <div className="mt-3 border-t border-slate-200 pt-3">
                              <button
                                onClick={() => setExpandedAircraftByRoute((prev) => ({ ...prev, [routeKey]: !aircraftExpanded }))}
                                className="w-full flex items-center justify-between rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-xs font-black uppercase tracking-wider text-slate-700"
                              >
                                <span>Aircraft Pricing</span>
                                <span>{aircraftExpanded ? 'Hide' : 'Show'}</span>
                              </button>

                              {aircraftExpanded && (
                                <div className="mt-2 space-y-2">
                                  {aircrafts.map((aircraft) => {
                                    const rate = route.aircraftRates?.find((r) => r.aircraftId === aircraft.id);
                                    return (
                                      <div key={aircraft.id} className="grid grid-cols-[1fr_100px_100px] gap-2 items-center">
                                        <div className="text-xs font-bold text-slate-700 truncate">{aircraft.tailNumber}</div>
                                        <input
                                          type="number"
                                          className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                                          placeholder="$/NM"
                                          value={rate?.ratePerNmUSD ?? 0}
                                          onChange={(e) => onUpsertRouteAircraftRate(route.id, aircraft.id, Number(e.target.value) || 0, rate?.ratePerHourUSD ?? 0)}
                                        />
                                        <input
                                          type="number"
                                          className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                                          placeholder="$/HR"
                                          value={rate?.ratePerHourUSD ?? 0}
                                          onChange={(e) => onUpsertRouteAircraftRate(route.id, aircraft.id, rate?.ratePerNmUSD ?? 0, Number(e.target.value) || 0)}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};
